import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Lock,
  Check,
  X,
  AlertTriangle,
  FileText,
  Shield,
  ClipboardCheck,
  GitBranch,
  Database,
  Activity,
  AlertOctagon,
  FileSearch,
  CheckCircle2,
  Circle,
  Info
} from 'lucide-react';

interface SOXControl {
  controlId: string;
  controlName: string;
  category: string;
  description: string;
  whyImpacted: string;
  objectives: string[];
  relevanceScore: number;
}

interface ControlActivity {
  id: string;
  activity: string;
  controlReference: string;
  category: string;
  frequency: string;
  responsible: string;
  status: 'pending' | 'completed';
  evidenceRequired: string;
}

interface SOPReference {
  code: string;
  name: string;
  section: string;
}

interface FailureScenario {
  scenario: string;
  description: string;
  impact: string;
  businessImpact: string;
  probability: string;
  mitigationControls: string[];
  escalationPath: string;
  recoveryTime: string;
  rollbackProcedure: string;
}

interface AuditorQuestion {
  category: string;
  question: string;
  suggestedResponse: string;
}

interface EnhancedComplianceRequirementsProps {
  complianceRequirements: {
    sox_controls?: {
      applicable: boolean;
      controls: SOXControl[];
      controlCount: number;
      itGeneralControlsCount: number;
      businessProcessControlsCount: number;
      riskLevel: 'low' | 'medium' | 'high';
      notes: string;
      requirements: string[];
    };
    control_activities?: {
      applicable: boolean;
      checklist: ControlActivity[];
      totalActivities: number;
      riskLevel: 'low' | 'medium' | 'high';
      notes: string;
      requirements: string[];
    };
    change_management?: {
      applicable: boolean;
      changeType: string;
      approvalLevels: string[];
      sopReferences: SOPReference[];
      trainingRequirements: {
        requiredRoles: string[];
        estimatedUsers: string;
        trainingDuration: string;
        trainingType: string;
      };
      testingPeriod: {
        duration: string;
        phases: { name: string; duration: string }[];
        parallelRunRequired: boolean;
        regressionRequired: boolean;
      };
      notificationRequirements: string[];
      riskLevel: 'low' | 'medium' | 'high';
      notes: string;
      requirements: string[];
    };
    data_governance?: {
      applicable: boolean;
      dataClassification: string;
      classificationLevel: {
        name: string;
        description: string;
        color: string;
      };
      dataTypesInvolved: { type: string; name: string; category: string }[];
      piiInvolved: boolean;
      financialDataInvolved: boolean;
      privacyRequirements: string[];
      retentionRequirements: { period: string; details: string };
      dataResidency: { considerations: string[]; requiresReview: boolean };
      applicableFrameworks: string[];
      riskLevel: 'low' | 'medium' | 'high';
      notes: string;
      requirements: string[];
    };
    validation_monitoring?: {
      applicable: boolean;
      preImplementation: {
        tests: { type: string; description: string; threshold: string }[];
        accuracyThreshold: string;
        signOffRequired: boolean;
      };
      pilotPhase: {
        required: boolean;
        duration: string;
        parallelRunRequired: boolean;
        rollbackTestRequired: boolean;
      };
      ongoingMonitoring: {
        kpis: { name: string; description: string; threshold: string; frequency: string; alertThreshold: string }[];
        qualitySampling: { rate: string; frequency: string; reviewedBy: string };
        reviewFrequency: string;
      };
      riskLevel: 'low' | 'medium' | 'high';
      notes: string;
      requirements: string[];
    };
    risk_mitigation?: {
      applicable: boolean;
      failureScenarios: FailureScenario[];
      escalationProcedures: {
        levels: { level: number; title: string; authority: string }[];
        timeframes: Record<string, string>;
      };
      rollbackPlan: {
        required: boolean;
        components: string[];
        maxRollbackTime: string;
        approvalRequired: boolean;
      };
      decisionAuthority: { level: number; title: string; authority: string; examples: string[] }[];
      riskLevel: 'low' | 'medium' | 'high';
      notes: string;
      requirements: string[];
    };
    regulatory_exam_prep?: {
      applicable: boolean;
      auditorQuestions: AuditorQuestion[];
      evidenceRequirements: {
        categories: string[];
        items: Record<string, string[]>;
        totalItems: number;
      };
      documentationStandards: {
        format: string[];
        content: string[];
        retention: string[];
      };
      auditReadiness: {
        preparationChecklist: string[];
        documentInventory: { document: string; location: string }[];
        reviewSchedule: string;
      };
      riskLevel: 'low' | 'medium' | 'high';
      notes: string;
      requirements: string[];
    };
    metadata?: {
      generatedAt: string;
      overallRiskLevel: 'low' | 'medium' | 'high';
    };
    // Legacy structure support
    sox_controls_legacy?: any;
    regulatory_frameworks?: any;
    audit_requirements?: any;
    data_privacy?: any;
    segregation_of_duties?: any;
    documentation?: any;
  };
  implementationComplexity: 'low' | 'medium' | 'high';
}

const EnhancedComplianceRequirements: React.FC<EnhancedComplianceRequirementsProps> = ({
  complianceRequirements,
  implementationComplexity
}) => {
  const [expanded, setExpanded] = useState(implementationComplexity === 'high');
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Detect if this is legacy or enhanced format
  const isEnhancedFormat = !!complianceRequirements?.metadata ||
                           !!complianceRequirements?.control_activities ||
                           !!complianceRequirements?.validation_monitoring;

  const getTotalRequirementsCount = () => {
    if (!complianceRequirements) return 0;

    let count = 0;
    const sections = [
      'sox_controls', 'control_activities', 'change_management',
      'data_governance', 'validation_monitoring', 'risk_mitigation', 'regulatory_exam_prep'
    ];

    sections.forEach(key => {
      const section = complianceRequirements[key as keyof typeof complianceRequirements] as any;
      if (section?.requirements && Array.isArray(section.requirements)) {
        count += section.requirements.length;
      }
    });

    return count;
  };

  const getRiskBadge = (riskLevel: string) => {
    const config = {
      high: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: AlertTriangle },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: AlertTriangle },
      low: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: Check }
    };
    const style = config[riskLevel as keyof typeof config] || config.low;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${style.bg} ${style.text} border ${style.border}`}>
        <Icon className="w-3 h-3" />
        {riskLevel.toUpperCase()}
      </span>
    );
  };

  const getClassificationBadge = (classification: string) => {
    const config: Record<string, { bg: string; text: string; border: string }> = {
      RESTRICTED: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
      CONFIDENTIAL: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
      INTERNAL: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
      PUBLIC: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' }
    };
    const style = config[classification?.toUpperCase()] || config.INTERNAL;

    return (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${style.bg} ${style.text} border ${style.border}`}>
        {classification}
      </span>
    );
  };

  const sections = [
    {
      key: 'sox_controls',
      title: 'SOX Controls Impacted',
      icon: Shield,
      color: 'purple'
    },
    {
      key: 'control_activities',
      title: 'Required Control Activities',
      icon: ClipboardCheck,
      color: 'blue'
    },
    {
      key: 'change_management',
      title: 'Change Management Requirements',
      icon: GitBranch,
      color: 'green'
    },
    {
      key: 'data_governance',
      title: 'Data Governance & Classification',
      icon: Database,
      color: 'orange'
    },
    {
      key: 'validation_monitoring',
      title: 'Validation & Monitoring',
      icon: Activity,
      color: 'cyan'
    },
    {
      key: 'risk_mitigation',
      title: 'Risk Mitigation & Failure Scenarios',
      icon: AlertOctagon,
      color: 'red'
    },
    {
      key: 'regulatory_exam_prep',
      title: 'Regulatory Examination Prep',
      icon: FileSearch,
      color: 'indigo'
    }
  ];

  const totalCount = getTotalRequirementsCount();
  const overallRisk = complianceRequirements?.metadata?.overallRiskLevel || 'medium';

  const renderSOXControls = (data: any) => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600">IT General Controls: <strong>{data.itGeneralControlsCount || 0}</strong></span>
          <span className="text-gray-600">Business Process Controls: <strong>{data.businessProcessControlsCount || 0}</strong></span>
        </div>

        {data.controls?.map((control: SOXControl, idx: number) => (
          <div key={idx} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-purple-200 text-purple-800 text-xs font-mono font-bold rounded">
                  {control.controlId}
                </span>
                <span className="font-semibold text-gray-900 text-sm">{control.controlName}</span>
              </div>
              <span className="text-xs text-gray-500">{control.category}</span>
            </div>
            <p className="text-xs text-gray-700 mb-2">{control.description}</p>
            <p className="text-xs text-purple-700 italic">Why Impacted: {control.whyImpacted}</p>
            {control.objectives?.length > 0 && (
              <div className="mt-2">
                <span className="text-xs font-semibold text-gray-600">Key Objectives:</span>
                <ul className="mt-1 space-y-0.5">
                  {control.objectives.slice(0, 2).map((obj, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                      <span className="text-purple-500 mt-0.5">•</span>
                      {obj}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderControlActivities = (data: any) => {
    if (!data) return null;

    return (
      <div className="space-y-2">
        <p className="text-xs text-gray-600 mb-3">
          {data.totalActivities} control activities required for implementation compliance
        </p>
        {data.checklist?.map((activity: ControlActivity, idx: number) => (
          <div key={idx} className="flex items-start gap-3 p-2 bg-gray-50 rounded border border-gray-200">
            <div className="mt-0.5">
              {activity.status === 'completed' ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              ) : (
                <Circle className="w-4 h-4 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-blue-600">{activity.id}</span>
                <span className="text-xs text-gray-500">|</span>
                <span className="text-xs text-gray-500">{activity.controlReference}</span>
              </div>
              <p className="text-sm text-gray-800">{activity.activity}</p>
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                <span>Frequency: {activity.frequency}</span>
                <span>Responsible: {activity.responsible}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderChangeManagement = (data: any) => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Change Type:</span>
          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded">
            {data.changeType}
          </span>
        </div>

        <div>
          <h5 className="text-xs font-semibold text-gray-700 mb-2">Approval Levels Required:</h5>
          <div className="flex flex-wrap gap-2">
            {data.approvalLevels?.map((level: string, idx: number) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                {idx + 1}. {level}
              </span>
            ))}
          </div>
        </div>

        {data.sopReferences?.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-700 mb-2">SOP References:</h5>
            {data.sopReferences.map((sop: SOPReference, idx: number) => (
              <div key={idx} className="text-xs text-gray-600 flex items-center gap-2 mb-1">
                <span className="font-mono text-blue-600">{sop.code}</span>
                <span>{sop.name}</span>
                <span className="text-gray-400">- {sop.section}</span>
              </div>
            ))}
          </div>
        )}

        {data.trainingRequirements && (
          <div className="bg-blue-50 p-3 rounded">
            <h5 className="text-xs font-semibold text-blue-800 mb-2">Training Requirements:</h5>
            <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
              <div>Users: {data.trainingRequirements.estimatedUsers}</div>
              <div>Duration: {data.trainingRequirements.trainingDuration}</div>
              <div>Type: {data.trainingRequirements.trainingType}</div>
              <div>Roles: {data.trainingRequirements.requiredRoles?.join(', ')}</div>
            </div>
          </div>
        )}

        {data.testingPeriod && (
          <div>
            <h5 className="text-xs font-semibold text-gray-700 mb-2">Testing Period: {data.testingPeriod.duration}</h5>
            <div className="flex items-center gap-2 text-xs">
              {data.testingPeriod.parallelRunRequired && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded">Parallel Run Required</span>
              )}
              {data.testingPeriod.regressionRequired && (
                <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded">Regression Testing Required</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDataGovernance = (data: any) => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">Classification:</span>
          {getClassificationBadge(data.dataClassification)}
          {data.piiInvolved && (
            <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded border border-red-300">
              PII Involved
            </span>
          )}
          {data.financialDataInvolved && (
            <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-semibold rounded border border-orange-300">
              Financial Data
            </span>
          )}
        </div>

        {data.dataTypesInvolved?.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-700 mb-2">Data Types Involved:</h5>
            <div className="flex flex-wrap gap-2">
              {data.dataTypesInvolved.map((dt: any, idx: number) => (
                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                  {dt.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {data.applicableFrameworks?.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-700 mb-2">Applicable Regulatory Frameworks:</h5>
            <div className="flex flex-wrap gap-2">
              {data.applicableFrameworks.map((fw: string, idx: number) => (
                <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs font-semibold rounded">
                  {fw}
                </span>
              ))}
            </div>
          </div>
        )}

        {data.retentionRequirements && (
          <div className="bg-orange-50 p-3 rounded">
            <h5 className="text-xs font-semibold text-orange-800 mb-1">Retention Requirements:</h5>
            <p className="text-xs text-orange-700">{data.retentionRequirements.period}</p>
            {data.retentionRequirements.details && (
              <p className="text-xs text-orange-600 mt-1">{data.retentionRequirements.details}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderValidationMonitoring = (data: any) => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        {data.preImplementation && (
          <div>
            <h5 className="text-xs font-semibold text-gray-700 mb-2">Pre-Implementation Testing:</h5>
            <div className="space-y-2">
              {data.preImplementation.tests?.map((test: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between text-xs bg-cyan-50 p-2 rounded">
                  <span className="font-medium text-gray-800">{test.type}</span>
                  <span className="text-cyan-700">Threshold: {test.threshold || test.customThreshold}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Accuracy Threshold: <strong>{data.preImplementation.accuracyThreshold}</strong>
            </p>
          </div>
        )}

        {data.pilotPhase?.required && (
          <div className="bg-yellow-50 p-3 rounded">
            <h5 className="text-xs font-semibold text-yellow-800 mb-2">Pilot Phase Required</h5>
            <div className="grid grid-cols-2 gap-2 text-xs text-yellow-700">
              <div>Duration: {data.pilotPhase.duration}</div>
              <div>Parallel Run: {data.pilotPhase.parallelRunRequired ? 'Yes' : 'No'}</div>
            </div>
          </div>
        )}

        {data.ongoingMonitoring?.kpis?.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-700 mb-2">Ongoing Monitoring KPIs:</h5>
            <div className="space-y-2">
              {data.ongoingMonitoring.kpis.slice(0, 4).map((kpi: any, idx: number) => (
                <div key={idx} className="text-xs bg-gray-50 p-2 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-800">{kpi.name}</span>
                    <span className="text-gray-500">{kpi.frequency}</span>
                  </div>
                  <p className="text-gray-600">Target: {kpi.threshold || kpi.customThreshold}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRiskMitigation = (data: any) => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        {data.failureScenarios?.slice(0, 3).map((scenario: FailureScenario, idx: number) => (
          <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-gray-900 text-sm">{scenario.scenario}</span>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                scenario.businessImpact === 'Critical' || scenario.impact === 'HIGH'
                  ? 'bg-red-200 text-red-800'
                  : scenario.impact === 'MEDIUM'
                  ? 'bg-yellow-200 text-yellow-800'
                  : 'bg-gray-200 text-gray-800'
              }`}>
                {scenario.businessImpact || scenario.impact}
              </span>
            </div>
            <p className="text-xs text-gray-700 mb-2">{scenario.description}</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-gray-500">Recovery Time:</span> {scenario.recoveryTime}</div>
              <div><span className="text-gray-500">Probability:</span> {scenario.probability}</div>
            </div>
            <div className="mt-2">
              <span className="text-xs text-gray-500">Escalation:</span>
              <span className="text-xs text-gray-700 ml-1">{scenario.escalationPath}</span>
            </div>
          </div>
        ))}

        {data.rollbackPlan && (
          <div className="bg-gray-50 p-3 rounded">
            <h5 className="text-xs font-semibold text-gray-700 mb-2">Rollback Plan</h5>
            <div className="text-xs text-gray-600">
              <p>Max Rollback Time: <strong>{data.rollbackPlan.maxRollbackTime}</strong></p>
              <p className="mt-1">Approval Required: {data.rollbackPlan.approvalRequired ? 'Yes' : 'No'}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRegulatoryExamPrep = (data: any) => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        {data.auditorQuestions?.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-700 mb-2">Common Auditor Questions:</h5>
            <div className="space-y-2">
              {data.auditorQuestions.slice(0, 4).map((q: AuditorQuestion, idx: number) => (
                <div key={idx} className="bg-indigo-50 p-2 rounded">
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-indigo-600 font-semibold">{q.category}:</span>
                    <p className="text-xs text-gray-800">{q.question}</p>
                  </div>
                  {q.suggestedResponse && (
                    <p className="text-xs text-indigo-600 mt-1 italic">
                      <Info className="w-3 h-3 inline mr-1" />
                      {q.suggestedResponse}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {data.evidenceRequirements && (
          <div>
            <h5 className="text-xs font-semibold text-gray-700 mb-2">
              Evidence Requirements ({data.evidenceRequirements.totalItems} items)
            </h5>
            <div className="flex flex-wrap gap-2">
              {data.evidenceRequirements.categories?.map((cat: string, idx: number) => (
                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded capitalize">
                  {cat}: {data.evidenceRequirements.items?.[cat]?.length || 0}
                </span>
              ))}
            </div>
          </div>
        )}

        {data.auditReadiness && (
          <div className="bg-indigo-50 p-3 rounded">
            <h5 className="text-xs font-semibold text-indigo-800 mb-2">Audit Readiness Review Schedule</h5>
            <p className="text-xs text-indigo-700">{data.auditReadiness.reviewSchedule}</p>
          </div>
        )}
      </div>
    );
  };

  const renderSectionContent = (sectionKey: string) => {
    const data = complianceRequirements?.[sectionKey as keyof typeof complianceRequirements];

    switch (sectionKey) {
      case 'sox_controls':
        return renderSOXControls(data);
      case 'control_activities':
        return renderControlActivities(data);
      case 'change_management':
        return renderChangeManagement(data);
      case 'data_governance':
        return renderDataGovernance(data);
      case 'validation_monitoring':
        return renderValidationMonitoring(data);
      case 'risk_mitigation':
        return renderRiskMitigation(data);
      case 'regulatory_exam_prep':
        return renderRegulatoryExamPrep(data);
      default:
        return null;
    }
  };

  return (
    <div className="mt-4">
      {/* Header */}
      <div
        className="border border-gray-200 rounded-lg p-4 cursor-pointer transition-colors hover:bg-gray-50 print:bg-white print:border-gray-300"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-gray-500" />
            <h4 className="text-sm font-bold text-gray-900">
              Compliance & Risk Requirements ({totalCount} requirements)
            </h4>
            {getRiskBadge(overallRisk)}
            {isEnhancedFormat && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                Enhanced
              </span>
            )}
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          )}
        </div>
        {!expanded && (
          <p className="text-xs text-gray-600 mt-2">
            Click to view SOX controls, data governance, validation requirements, and regulatory examination preparation
          </p>
        )}
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="mt-3 space-y-3">
          {sections.map(section => {
            const sectionData = complianceRequirements?.[section.key as keyof typeof complianceRequirements] as any;
            if (!sectionData?.applicable && sectionData?.applicable !== undefined) return null;

            const Icon = section.icon;
            const isActive = activeSection === section.key;

            return (
              <div
                key={section.key}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm print:break-inside-avoid"
              >
                <div
                  className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${
                    isActive ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveSection(isActive ? null : section.key)}
                >
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 text-${section.color}-600`} />
                    <span className="font-semibold text-sm text-gray-900">{section.title}</span>
                    {sectionData?.riskLevel && getRiskBadge(sectionData.riskLevel)}
                  </div>
                  {isActive ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </div>

                {isActive && (
                  <div className="p-4 border-t border-gray-200">
                    {sectionData?.notes && (
                      <p className="text-xs text-gray-600 italic mb-3">{sectionData.notes}</p>
                    )}
                    {renderSectionContent(section.key)}

                    {sectionData?.requirements?.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2">Key Requirements:</h5>
                        <ul className="space-y-1">
                          {sectionData.requirements.map((req: string, idx: number) => (
                            <li key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EnhancedComplianceRequirements;
